// File: src/pages/AddRawProduct.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { IoIosAdd } from "react-icons/io";
import swal from "sweetalert";

// ---- APIs & helpers (your app layer in ../import) ----
import {
  AllCategoryList,
  CreateCategory,
  CreateSubCategory,
  CreateWarehousesave,
  _Get,
  _PostSave,
  _GetDataBydatabaseName,
  _GetByUrl,
} from "../import/ApiEndPoint/ApiCalling";

import {
  Create_Warehouse_List,
  Bulk_Update_Product,
  Bulk_Upload_Product,
  PurchaseProductList_Product,
  addRawMaterial,
  viewUnits,
  // Production Product Section endpoint you provided
  viewProductionProductSection,
} from "../import/ApiEndPoint/Api";

// -------- tiny CSS injection (page scoped) ----------
const injectOnce = () => {
  if (document.getElementById("arp-inline-css")) return;
  const style = document.createElement("style");
  style.id = "arp-inline-css";
  style.innerHTML = `
    .arp-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .arp-title{ font-weight:800; text-transform:uppercase; font-size:18px; letter-spacing:.02em; }
    .arp-help{ color:#64748b; font-size:12px; }
    .arp-chip{ display:inline-flex; gap:6px; align-items:center; padding:3px 8px; border-radius:999px; background:#e8f0ff; border:1px solid #c7d2fe; cursor:pointer }
    .arp-box{ border:1px solid #e5e7eb; border-radius:14px; padding:12px; background:#fff; }
    .arp-total{ border:1px solid #e5e7eb; border-radius:12px; padding:10px; }
  `;
  document.head.appendChild(style);
};

// -------- helpers ----------
const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const money = (n) => (Number.isFinite(+n) ? Number(n).toFixed(2) : "0.00");

export default function AddRawProduct() {
  useEffect(() => injectOnce(), []);
  const navigate = useNavigate();

  // UI
  const [loading, setLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);

  // Lists
  const [wareHouseList, setWareHouseList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subcatlist, setSubcatlist] = useState([]);
  const [subStepslist, setSubStepslist] = useState([]);
  const [units, setUnits] = useState([]);
  const [purchaseProductList, setPurchaseProductList] = useState([]);
  const [rawProducts, setRawProducts] = useState([]);

  // 🔹 Production Product Sections (for dropdown)
  const [prodSections, setProdSections] = useState([]); // [{_id,name,description,status}]

  // Modals
  const [warehouseModal, setWarehouseModal] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [subCategoryModal, setSubCategoryModal] = useState(false);
  const [subStepsModal, setSubStepsModal] = useState(false);

  // Inline modal form data
  const [whForm, setWhForm] = useState({
    warehouseName: "",
    warehouseAddress: "",
    warehouseMobile: "",
    landlineNumber: "",
    status: "Active",
  });
  const [catForm, setCatForm] = useState({
    category_name: "",
    description: "",
    status: "Active",
  });
  const [subCatForm, setSubCatForm] = useState({
    category: "",
    subcategory_name: "",
    Description: "",
    status: "Active",
  });
  const [stepForm, setStepForm] = useState({
    category_name: "",
    step_name: "",
    Description: "",
    status: "Active",
    processName: "",
  });

  // Main product data
  const [errors, setErrors] = useState({});
  const [isLastStep, setIsLastStep] = useState(false);

  const [formValues, setFormValues] = useState([{ unit: "", qty: "" }]); // Units rows
  const [Data, setData] = useState({
    id: "",
    database: "",
    productSection: "", // <--- IMPORTANT: correct key expected by your model
    warehouseId: "",
    warehouseName: "",
    category: "",
    SubCategory: "",
    SubSteps: "",
    NextSubStep: "",
    Product_Title: "",
    Opening_Stock: "",
    MIN_stockalert: "",
    status: "Active",
    stockUnit: "",
    secondaryUnit: "",
    rawValues: [
      {
        rawMaterial: "",
        rawProductId: "",
        unit: "",
        qty: "",
        rate: "",
        Price: "",
      },
    ],
    targetValues: [{ step: "", rawMaterial: "", unit: [] }],
    expensePercent: 17,
  });

  // Bulk state
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkType, setBulkType] = useState("New Upload"); // or "Update"

  // ---------- Fetch lists ----------
  const hydrate = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const db = user?.database;
    if (!db) return;

    setLoading(true);
    try {
      const [whRes, catRes, unitRes, purchRes, prodRes] = await Promise.all([
        _Get(Create_Warehouse_List, db),
        AllCategoryList(user?._id, db),
        _GetByUrl(viewUnits, db),
        _Get(PurchaseProductList_Product, db),
        _GetDataBydatabaseName(db),
      ]);

      setWareHouseList(Array.isArray(whRes?.Warehouse) ? whRes.Warehouse : []);
      setCategoryList(Array.isArray(catRes?.Category) ? catRes.Category : []);
      setUnits(unitRes?.dynamicUnit?.Units || []);
      setPurchaseProductList(
        Array.isArray(purchRes?.Product) ? purchRes.Product : []
      );
      setRawProducts(Array.isArray(prodRes?.Product) ? prodRes.Product : []);
      setData((p) => ({ ...p, database: db }));

      // 🔹 Load Production Product Sections via your endpoint (GET /.../:database)
      const secRes = await _GetByUrl(viewProductionProductSection, db);
      const list = Array.isArray(secRes?.ProductionProductSection)
        ? secRes.ProductionProductSection
        : [];
      setProdSections(list);
    } catch (e) {
      console.error("[AddRawProduct] fetch error:", e);
      swal("Failed to load supporting data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hydrate();
  }, []);

  // ---------- Handlers ----------
  const onField = (e) => {
    const { name, value } = e.target;

    if (name === "warehouseId") {
      const wh = wareHouseList.find((w) => String(w?._id) === String(value));
      setData((p) => ({
        ...p,
        warehouseId: value,
        warehouseName: wh?.warehouseName || "",
      }));
      return;
    }

    if (name === "Product_Title") {
      const filteredId = String(value).replace(/\s/g, "");
      setData((p) => ({
        ...p,
        id: filteredId,
        Product_Title: String(value).toUpperCase(),
      }));
      return;
    }

    if (name === "category") {
      const cat = categoryList.find((c) => c?.name === value);
      setSubcatlist(Array.isArray(cat?.subcategories) ? cat.subcategories : []);
      setSubStepslist(Array.isArray(cat?.steps) ? cat.steps : []);
      setData((p) => ({
        ...p,
        category: value,
        SubCategory: "",
        SubSteps: "",
        NextSubStep: "",
      }));
      setIsLastStep(false);
      return;
    }

    if (name === "NextSubStep") {
      const idx = subStepslist.findIndex((s) => s?.step_Name === value);
      setIsLastStep(idx >= 0 && idx + 1 === subStepslist.length);
      setData((p) => ({ ...p, NextSubStep: value }));
      return;
    }

    setData((p) => ({ ...p, [name]: value }));
  };

  // Units section
  const handleUnitRow = (i, e) => {
    const { name, value } = e.target;
    const list = formValues.slice();
    list[i][name] = name === "qty" ? toNum(value, "") : value;
    setFormValues(list);
  };
  const addUnitRow = () => setFormValues((p) => [...p, { unit: "", qty: "" }]);
  const removeUnitRow = (i) => {
    const list = formValues.slice();
    list.splice(i, 1);
    setFormValues(list);
  };

  // Costing rows
  const rawTotal = useMemo(
    () =>
      Data.rawValues.reduce(
        (acc, r) => acc + (r.Price === "" ? 0 : Number(r.Price || 0)),
        0
      ),
    [Data.rawValues]
  );

  const addRawRow = () =>
    setData((p) => ({
      ...p,
      rawValues: [
        ...p.rawValues,
        {
          rawMaterial: "",
          rawProductId: "",
          unit: "",
          qty: "",
          rate: "",
          Price: "",
        },
      ],
    }));
  const removeRawRow = (i) =>
    setData((p) => {
      const list = p.rawValues.slice();
      list.splice(i, 1);
      return { ...p, rawValues: list };
    });

  const onRawMaterial = (i, e) => {
    const { value } = e.target;
    const list = Data.rawValues.slice();
    const selected = purchaseProductList.find(
      (p) => p?.Product_Title === value
    );
    if (selected) {
      const perUnitRate = Number(selected?.Purchase_Rate || 0);
      list[i] = {
        ...list[i],
        rawMaterial: value,
        rawProductId: selected?._id,
        unit: selected?.secondaryUnit || selected?.stockUnit || "",
        qty: 1,
        rate: perUnitRate.toFixed(4),
        Price: Number(1 * perUnitRate).toFixed(4),
      };
    } else {
      list[i] = { ...list[i], rawMaterial: value };
    }
    setData((p) => ({ ...p, rawValues: list }));
  };

  const onRawQty = (i, e) => {
    const qty = toNum(e.target.value, 0);
    const list = Data.rawValues.slice();
    const row = list[i];
    if (!row) return;

    const base = purchaseProductList.find(
      (p) => p?.Product_Title === row.rawMaterial
    );
    if (base) {
      const baseRate = Number(base?.Purchase_Rate || 0);
      const secSize = Number(base?.secondarySize || 1);
      const perUnit = secSize ? baseRate / secSize : baseRate;
      const total = qty * perUnit;
      list[i] = { ...row, qty, Price: Number(total).toFixed(6) };
      setData((p) => ({ ...p, rawValues: list }));
    } else {
      list[i] = { ...row, qty, Price: "0.0000" };
      setData((p) => ({ ...p, rawValues: list }));
    }
  };

  // Target section
  const addTargetRow = () =>
    setData((p) => ({
      ...p,
      targetValues: [
        ...p.targetValues,
        { step: "", rawMaterial: "", unit: [] },
      ],
    }));
  const removeTargetRow = (i) =>
    setData((p) => {
      const list = p.targetValues.slice();
      list.splice(i, 1);
      return { ...p, targetValues: list };
    });

  const onTargetStep = (i, e) => {
    const { value } = e.target;
    const list = Data.targetValues.slice();
    list[i] = { ...list[i], step: value };
    setData((p) => ({ ...p, targetValues: list }));
  };

  const onTargetMaterial = (i, e) => {
    const { value } = e.target;
    const list = Data.targetValues.slice();
    const selected = rawProducts.find((p) => p?.Product_Title === value);
    list[i] = {
      ...list[i],
      rawMaterial: value,
      unit: Array.isArray(selected?.Units) ? [...selected.Units] : [],
    };
    setData((p) => ({ ...p, targetValues: list }));
  };

  const onTargetUnitQty = (rowIndex, unitIndex, e) => {
    const qty = toNum(e.target.value, 0);
    const list = Data.targetValues.slice();
    const row = list[rowIndex];
    if (!row || !Array.isArray(row.unit)) return;

    if (!row.unit.some((u) => u.__convInit)) {
      row.unit.forEach((u) => {
        u.conversionFactor = row.unit.map((v) => ({
          unit: v.unit,
          factor: u.qty && v.qty ? Number(v.qty) / Number(u.qty) : 0,
        }));
        u.__convInit = true;
      });
    }

    row.unit[unitIndex].qty = qty;
    row.unit[unitIndex].value = qty;

    const factors = row.unit[unitIndex].conversionFactor || [];
    row.unit.forEach((u) => {
      if (u === row.unit[unitIndex]) return;
      const f = factors.find((x) => x.unit === u.unit);
      if (f) {
        u.qty = Number(qty * f.factor).toFixed(4);
        u.value = Number(qty * f.factor).toFixed(4);
      }
    });

    list[rowIndex] = { ...row };
    setData((p) => ({ ...p, targetValues: list }));
  };

  // ---------- Validation ----------
  const validate = () => {
    const errs = {};
    if (!Data.warehouseId) errs.warehouseId = "Please select a Warehouse";

    const stockUnitChosen = formValues.some((u) => u.unit === Data.stockUnit);
    if (!stockUnitChosen)
      errs.stockUnit = "Unit mismatch with selected Stock Unit";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ---------- Submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData") || "{}");

    if (bulkMode) {
      if (!bulkUploadFile) return swal("Please choose a file");
      try {
        const formdata = new FormData();
        formdata.append("file", bulkUploadFile);
        formdata.append("database", user?.database);

        const url =
          bulkType === "Update"
            ? `${Bulk_Update_Product}/${user?.database}`
            : `${Bulk_Upload_Product}/${user?.database}`;

        setLoading(true);
        await _PostSave(url, formdata);
        setLoading(false);
        swal("Imported successfully");
        navigate(-1);
      } catch (err) {
        console.error(err);
        setLoading(false);
        swal("Something went wrong");
      }
      return;
    }

    if (!validate()) return;

    const payload = {
      id: Data.id,
      database: user?.database,

      // 🔹 IMPORTANT: send exactly what you selected.
      // If you chose the fixed option RAW MATERIAL, this will be "RAW_MATERIAL".
      productSection: Data.productSection || "",

      warehouse: Data.warehouseId,
      warehouseName: Data.warehouseName,
      category: Data.category,
      SubCategory: Data.SubCategory,
      SubStep: Data.SubSteps,
      NextSubStep: Data.NextSubStep,
      Product_Title: Data.Product_Title,
      Opening_Stock: toNum(Data.Opening_Stock),
      MIN_stockalert: toNum(Data.MIN_stockalert),
      status: Data.status,
      stockUnit: Data.stockUnit,
      secondaryUnit: Data.secondaryUnit,
      Units: formValues,
      created_by: user?._id,

      rawValues: Data.rawValues,
      targetValues: Data.targetValues,
      grossTotal: Number(rawTotal.toFixed(2)),
      expensePercentage: Number(Data.expensePercent),
      netAmount: Number(
        (rawTotal * (1 + toNum(Data.expensePercent) / 100)).toFixed(2)
      ),
    };

    try {
      setLoading(true);
      const res = await _PostSave(addRawMaterial, payload);
      setLoading(false);
      if (res?.status) {
        swal("Product created successfully");
        navigate(-1);
      } else {
        swal(res?.message || "Failed to create");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      const msg = err?.response?.data?.message || "Something went wrong";
      swal(msg);
    }
  };

  // ---------- Modals toggles ----------
  const toggleWarehouse = () => setWarehouseModal((v) => !v);
  const toggleCategory = () => setCategoryModal((v) => !v);
  const toggleSubCategory = () => setSubCategoryModal((v) => !v);
  const toggleSteps = () => setSubStepsModal((v) => !v);

  if (loading && !wareHouseList.length && !categoryList.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  // Build Product Section dropdown options:
  // 1) fixed "RAW MATERIAL"
  // 2) all sections fetched from API
  const sectionOptions = [
    { _id: "RAW_MATERIAL", name: "PRODUCTION RAW PRODUCT" }, // <-- fixed option
    ...prodSections,
  ];

  return (
    <>
      <Card className="arp-card">
        <CardBody>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <div className="arp-title">Add Raw Product</div>
            <div style={{ flex: 1 }} />
            <Button
              size="sm"
              color={bulkMode ? "secondary" : "primary"}
              onClick={() => setBulkMode((v) => !v)}
            >
              {bulkMode ? "Back" : "Bulk Upload"}
            </Button>
            <Button size="sm" color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>

          {/* ================= BULK UPLOAD ================ */}
          {bulkMode ? (
            <Form
              onSubmit={onSubmit}
              className="arp-box"
              style={{ marginTop: 8 }}
            >
              <Row>
                <Col md="3" sm="6">
                  <Label className="mb-1">Type of Upload</Label>
                  <div onChange={(e) => setBulkType(e.target.value)}>
                    <label className="me-3">
                      <input
                        type="radio"
                        name="bulkType"
                        value="New Upload"
                        defaultChecked={bulkType === "New Upload"}
                      />{" "}
                      New Upload
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="bulkType"
                        value="Update"
                        defaultChecked={bulkType === "Update"}
                      />{" "}
                      Update
                    </label>
                  </div>
                </Col>
                <Col md="4" sm="6">
                  <FormGroup>
                    <Label>Bulk Import File</Label>
                    <Input
                      type="file"
                      onChange={(e) =>
                        setBulkUploadFile(e.target.files?.[0] || null)
                      }
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? "Importing..." : "Import"}
              </Button>
            </Form>
          ) : (
            // ================= CREATE SINGLE PRODUCT ================
            <Form onSubmit={onSubmit}>
              {/* Row 1 */}
              <Row className="arp-box" style={{ marginBottom: 12 }}>
                {/* 🔹 Product Section (now includes RAW MATERIAL fixed option) */}
                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>Product Section</Label>
                  <Input
                    type="select"
                    name="productSection"
                    value={Data.productSection}
                    onChange={onField}
                  >
                    <option value="">-- Select Section --</option>
                    {sectionOptions.map((s) => (
                      <option key={s?._id} value={s?._id}>
                        {s?.name}
                      </option>
                    ))}
                  </Input>
                  <div className="arp-help">
                    Includes fixed “RAW MATERIAL” + API sections
                  </div>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Warehouse <span style={{ color: "red" }}>*</span>
                  </Label>{" "}
                  <span className="arp-chip" onClick={toggleWarehouse}>
                    <IoIosAdd size={16} /> Add
                  </span>
                  <Input
                    type="select"
                    name="warehouseId"
                    value={Data.warehouseId}
                    onChange={onField}
                    style={{
                      border: errors.warehouseId ? "1px solid red" : undefined,
                    }}
                    required
                  >
                    <option value="">-- Select Warehouse --</option>
                    {wareHouseList.map((w) => (
                      <option key={w?._id} value={w?._id}>
                        {w?.warehouseName}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Category <span style={{ color: "red" }}>*</span>
                  </Label>{" "}
                  <span className="arp-chip" onClick={toggleCategory}>
                    <IoIosAdd size={16} /> Add
                  </span>
                  <Input
                    type="select"
                    name="category"
                    value={Data.category}
                    onChange={onField}
                    required
                  >
                    <option value="">-- Select Category --</option>
                    {categoryList.map((c) => (
                      <option key={c?._id} value={c?.name}>
                        {c?.name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    SubCategory <span style={{ color: "red" }}>*</span>
                  </Label>{" "}
                  <span className="arp-chip" onClick={toggleSubCategory}>
                    <IoIosAdd size={16} /> Add
                  </span>
                  <Input
                    type="select"
                    name="SubCategory"
                    value={Data.SubCategory}
                    onChange={onField}
                    required
                  >
                    <option value="">-- Select SubCategory --</option>
                    {subcatlist.map((s) => (
                      <option key={s?._id} value={s?.name}>
                        {s?.name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Step <span style={{ color: "red" }}>*</span>
                  </Label>{" "}
                  <span className="arp-chip" onClick={toggleSteps}>
                    <IoIosAdd size={16} /> Add
                  </span>
                  <Input
                    type="select"
                    name="SubSteps"
                    value={Data.SubSteps}
                    onChange={onField}
                    required
                  >
                    <option value="">-- Select Step --</option>
                    {subStepslist.map((s) => (
                      <option key={s?._id} value={s?.step_Name}>
                        {s?.step_Name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Raw Step <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="NextSubStep"
                    value={Data.NextSubStep}
                    onChange={onField}
                    required
                  >
                    <option value="">-- Select Raw Step --</option>
                    {subStepslist.map((s) => (
                      <option key={s?._id} value={s?.step_Name}>
                        {s?.step_Name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Product Name <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    name="Product_Title"
                    value={Data.Product_Title}
                    onChange={onField}
                    placeholder="Enter product name"
                    required
                  />
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Product Min Stock <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="MIN_stockalert"
                    value={Data.MIN_stockalert}
                    onChange={onField}
                    placeholder="Min stock alert"
                    required
                  />
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Stock Unit <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="stockUnit"
                    value={Data.stockUnit}
                    onChange={onField}
                    style={{
                      border: errors.stockUnit ? "1px solid red" : undefined,
                    }}
                    required
                  >
                    <option value="">-- Select --</option>
                    {units.map((u) => (
                      <option key={u?.unitId} value={u?.unitId}>
                        {u?.unitName}
                      </option>
                    ))}
                  </Input>
                  {errors.stockUnit && (
                    <div className="arp-help" style={{ color: "red" }}>
                      {errors.stockUnit}
                    </div>
                  )}
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Secondary Unit <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="secondaryUnit"
                    value={Data.secondaryUnit}
                    onChange={onField}
                    required
                  >
                    <option value="">-- Select --</option>
                    {units.map((u) => (
                      <option key={u?.unitId} value={u?.unitId}>
                        {u?.unitName}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label>
                    Opening Stock <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    name="Opening_Stock"
                    value={Data.Opening_Stock}
                    onChange={onField}
                    placeholder="Opening stock"
                    required
                  />
                </Col>

                <Col lg="3" md="6" sm="12" className="mt-1">
                  <Label className="mb-1">Status</Label>
                  <div
                    onChange={(e) =>
                      setData((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <label className="me-3">
                      <input
                        type="radio"
                        name="status"
                        value="Active"
                        defaultChecked={Data.status === "Active"}
                      />{" "}
                      Active
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="status"
                        value="Deactive"
                        defaultChecked={Data.status === "Deactive"}
                      />{" "}
                      Deactive
                    </label>
                  </div>
                </Col>

                {errors.unitPolicy && (
                  <Col xs="12" className="mt-1">
                    <div className="arp-help" style={{ color: "red" }}>
                      {errors.unitPolicy}
                    </div>
                  </Col>
                )}
              </Row>

              {/* Units editor */}
              <div className="arp-box" style={{ marginBottom: 12 }}>
                <div
                  className="arp-title"
                  style={{ fontSize: 14, marginBottom: 8 }}
                >
                  Units
                </div>
                {formValues.map((row, i) => (
                  <Row key={`unit-${i}`} className="align-items-end">
                    <Col lg="4" md="6" sm="12" className="mt-1">
                      <Label>Unit {i + 1}</Label>
                      <Input
                        type="select"
                        name="unit"
                        value={row.unit}
                        onChange={(e) => handleUnitRow(i, e)}
                        required
                      >
                        <option value="">-- Select --</option>
                        {units.map((u) => (
                          <option key={u?.unitId} value={u?.unitId}>
                            {u?.unitName}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col lg="4" md="6" sm="12" className="mt-1">
                      <Label>Quantity {i + 1}</Label>
                      <Input
                        type="number"
                        step="0.00001"
                        min="0"
                        name="qty"
                        value={row.qty}
                        onChange={(e) => handleUnitRow(i, e)}
                        placeholder="Enter Qty"
                        required
                      />
                    </Col>
                    <Col lg="2" md="3" sm="4" className="mt-1">
                      {i > 0 && (
                        <Button color="danger" onClick={() => removeUnitRow(i)}>
                          ❌
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
                <Button color="primary" className="mt-2" onClick={addUnitRow}>
                  Add
                </Button>
              </div>

              {/* Costing & Target sections if last step */}
              {isLastStep && (
                <>
                  {/* Costing */}
                  <div className="arp-box" style={{ marginBottom: 12 }}>
                    <div
                      className="arp-title"
                      style={{ fontSize: 14, marginBottom: 8 }}
                    >
                      Costing
                    </div>
                    {Data.rawValues.map((row, i) => (
                      <Row key={`raw-${i}`} className="align-items-end">
                        <Col lg="3" md="6" sm="12" className="mt-1">
                          <Label>Raw Material</Label>
                          <Input
                            type="select"
                            value={row.rawMaterial}
                            onChange={(e) => onRawMaterial(i, e)}
                            required
                          >
                            <option value="">-- Select --</option>
                            {purchaseProductList.map((p) => (
                              <option key={p?._id} value={p?.Product_Title}>
                                {p?.Product_Title}
                              </option>
                            ))}
                          </Input>
                        </Col>
                        <Col lg="2" md="6" sm="12" className="mt-1">
                          <Label>Units</Label>
                          <Input readOnly value={row.unit || ""} />
                        </Col>
                        <Col lg="2" md="6" sm="12" className="mt-1">
                          <Label>Qty</Label>
                          <Input
                            type="number"
                            step="0.00001"
                            min="0"
                            value={row.qty}
                            onChange={(e) => onRawQty(i, e)}
                            required
                          />
                        </Col>
                        <Col lg="2" md="6" sm="12" className="mt-1">
                          <Label>Rate</Label>
                          <Input readOnly value={row.rate || ""} />
                        </Col>
                        <Col lg="2" md="6" sm="12" className="mt-1">
                          <Label>Price</Label>
                          <Input readOnly value={row.Price || ""} />
                        </Col>
                        <Col lg="1" md="2" sm="3" className="mt-1">
                          {i > 0 && (
                            <Button
                              color="danger"
                              onClick={() => removeRawRow(i)}
                            >
                              ❌
                            </Button>
                          )}
                        </Col>
                      </Row>
                    ))}

                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <Button color="primary" onClick={addRawRow}>
                        Add
                      </Button>
                      <div className="arp-total">
                        <div className="d-flex justify-content-between">
                          <span>Gross Total:</span>
                          <strong>Rs {money(rawTotal)}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span>Expense %:</span>
                          <Input
                            type="number"
                            value={Data.expensePercent}
                            onChange={(e) =>
                              setData((p) => ({
                                ...p,
                                expensePercent: toNum(e.target.value, 0),
                              }))
                            }
                            style={{ width: 70, textAlign: "right" }}
                          />
                        </div>
                        <div className="d-flex justify-content-between mt-2">
                          <span>Net Amount:</span>
                          <strong>
                            Rs{" "}
                            {money(
                              rawTotal * (1 + toNum(Data.expensePercent) / 100)
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Target Required */}
                  <div className="arp-box" style={{ marginBottom: 12 }}>
                    <div
                      className="arp-title"
                      style={{ fontSize: 14, marginBottom: 8 }}
                    >
                      Target Required
                    </div>

                    {Data.targetValues.map((row, i) => (
                      <div
                        key={`targ-${i}`}
                        style={{
                          borderBottom: "1px dashed #e5e7eb",
                          paddingBottom: 10,
                          marginBottom: 10,
                        }}
                      >
                        <Row className="align-items-end">
                          <Col lg="3" md="6" sm="12" className="mt-1">
                            <Label>Process Step</Label>
                            <Input
                              type="select"
                              value={row.step}
                              onChange={(e) => onTargetStep(i, e)}
                              required
                            >
                              <option value="">-- Select --</option>
                              {subStepslist.map((s) => (
                                <option key={s?._id} value={s?.step_Name}>
                                  {s?.step_Name}
                                </option>
                              ))}
                            </Input>
                          </Col>

                          <Col lg="4" md="6" sm="12" className="mt-1">
                            <Label>Raw Material</Label>
                            <Input
                              type="select"
                              value={row.rawMaterial}
                              onChange={(e) => onTargetMaterial(i, e)}
                              required
                            >
                              <option value="">-- Select --</option>
                              {rawProducts.map((p) => (
                                <option key={p?._id} value={p?.Product_Title}>
                                  {p?.Product_Title}
                                </option>
                              ))}
                            </Input>
                          </Col>

                          <Col lg="2" md="3" sm="4" className="mt-1">
                            {i > 0 && (
                              <Button
                                color="danger"
                                onClick={() => removeTargetRow(i)}
                              >
                                ❌
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {Array.isArray(row.unit) && row.unit.length > 0 && (
                          <Row className="mt-1">
                            {row.unit.map((u, ui) => (
                              <Col
                                key={`u-${ui}`}
                                lg="2"
                                md="3"
                                sm="6"
                                className="mt-1"
                              >
                                <Label>{u?.unit}</Label>
                                <Input
                                  type="number"
                                  step="0.00001"
                                  min="0"
                                  value={u?.qty || ""}
                                  onChange={(e) => onTargetUnitQty(i, ui, e)}
                                />
                              </Col>
                            ))}
                          </Row>
                        )}
                      </div>
                    ))}

                    <Button color="primary" onClick={addTargetRow}>
                      Add
                    </Button>
                  </div>
                </>
              )}

              <div className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </Form>
          )}
        </CardBody>
      </Card>

      {/* ---------- Modals ---------- */}
      {/* Warehouse */}
      <Modal isOpen={warehouseModal} toggle={toggleWarehouse} backdrop="static">
        <ModalHeader toggle={toggleWarehouse}>Create Warehouse</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              const user = JSON.parse(localStorage.getItem("userData") || "{}");
              try {
                await CreateWarehousesave({
                  warehouseName: whForm.warehouseName,
                  mobileNo: whForm.warehouseMobile,
                  address: whForm.warehouseAddress,
                  landlineNumber: whForm.landlineNumber,
                  database: user?.database,
                  status: whForm.status,
                });
                swal("Warehouse saved");
                setWhForm({
                  warehouseName: "",
                  warehouseAddress: "",
                  warehouseMobile: "",
                  landlineNumber: "",
                  status: "Active",
                });
                toggleWarehouse();
                await hydrate();
              } catch (e) {
                console.error(e);
                swal("Failed to save warehouse");
              }
            }}
          >
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    value={whForm.warehouseName}
                    onChange={(e) =>
                      setWhForm((p) => ({
                        ...p,
                        warehouseName: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Address</Label>
                  <Input
                    value={whForm.warehouseAddress}
                    onChange={(e) =>
                      setWhForm((p) => ({
                        ...p,
                        warehouseAddress: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Mobile</Label>
                  <Input
                    type="number"
                    value={whForm.warehouseMobile}
                    onChange={(e) =>
                      setWhForm((p) => ({
                        ...p,
                        warehouseMobile: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Landline</Label>
                  <Input
                    type="number"
                    value={whForm.landlineNumber}
                    onChange={(e) =>
                      setWhForm((p) => ({
                        ...p,
                        landlineNumber: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <Label className="mb-1">Status</Label>
                <div
                  onChange={(e) =>
                    setWhForm((p) => ({ ...p, status: e.target.value }))
                  }
                >
                  <label className="me-3">
                    <input
                      type="radio"
                      name="whstatus"
                      value="Active"
                      defaultChecked={whForm.status === "Active"}
                    />{" "}
                    Active
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="whstatus"
                      value="Deactive"
                      defaultChecked={whForm.status === "Deactive"}
                    />{" "}
                    Deactive
                  </label>
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" type="submit">
                Submit
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Category */}
      <Modal isOpen={categoryModal} toggle={toggleCategory} backdrop="static">
        <ModalHeader toggle={toggleCategory}>Create Category</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              const user = JSON.parse(localStorage.getItem("userData") || "{}");
              try {
                const fd = new FormData();
                fd.append("created_by", user?._id);
                fd.append("name", catForm.category_name);
                fd.append("description", catForm.description);
                fd.append("status", catForm.status);
                await CreateCategory(fd);
                swal("Category created");
                setCatForm({
                  category_name: "",
                  description: "",
                  status: "Active",
                });
                toggleCategory();
                await hydrate();
              } catch (e) {
                console.error(e);
                swal("Failed to create category");
              }
            }}
          >
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Category Name</Label>
                  <Input
                    value={catForm.category_name}
                    onChange={(e) =>
                      setCatForm((p) => ({
                        ...p,
                        category_name: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    value={catForm.description}
                    onChange={(e) =>
                      setCatForm((p) => ({ ...p, description: e.target.value }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <Label className="mb-1">Status</Label>
                <div
                  onChange={(e) =>
                    setCatForm((p) => ({ ...p, status: e.target.value }))
                  }
                >
                  <label className="me-3">
                    <input
                      type="radio"
                      name="cstatus"
                      value="Active"
                      defaultChecked={catForm.status === "Active"}
                    />{" "}
                    Active
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="cstatus"
                      value="Deactive"
                      defaultChecked={catForm.status === "Deactive"}
                    />{" "}
                    Deactive
                  </label>
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" type="submit">
                Submit
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* SubCategory */}
      <Modal
        isOpen={subCategoryModal}
        toggle={toggleSubCategory}
        backdrop="static"
      >
        <ModalHeader toggle={toggleSubCategory}>Create SubCategory</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              const user = JSON.parse(localStorage.getItem("userData") || "{}");
              try {
                const fd = new FormData();
                fd.append("created_by", user?._id);
                fd.append("name", subCatForm.subcategory_name);
                fd.append("unitType", "");
                fd.append("category", subCatForm.category);
                fd.append("description", subCatForm.Description);
                fd.append("status", subCatForm.status);
                await CreateSubCategory(fd);
                swal("Subcategory created");
                setSubCatForm({
                  category: "",
                  subcategory_name: "",
                  Description: "",
                  status: "Active",
                });
                toggleSubCategory();
                await hydrate();
              } catch (e) {
                console.error(e);
                swal("Failed to create subcategory");
              }
            }}
          >
            <Row>
              <Col md="6">
                <Label>Category</Label>
                <Input
                  type="select"
                  value={subCatForm.category}
                  onChange={(e) =>
                    setSubCatForm((p) => ({ ...p, category: e.target.value }))
                  }
                  required
                >
                  <option value="">-- Select Category --</option>
                  {categoryList.map((c) => (
                    <option key={c?._id} value={c?._id}>
                      {c?.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>SubCategory Name</Label>
                  <Input
                    value={subCatForm.subcategory_name}
                    onChange={(e) =>
                      setSubCatForm((p) => ({
                        ...p,
                        subcategory_name: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    value={subCatForm.Description}
                    onChange={(e) =>
                      setSubCatForm((p) => ({
                        ...p,
                        Description: e.target.value,
                      }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <Label className="mb-1">Status</Label>
                <div
                  onChange={(e) =>
                    setSubCatForm((p) => ({ ...p, status: e.target.value }))
                  }
                >
                  <label className="me-3">
                    <input
                      type="radio"
                      name="scstatus"
                      value="Active"
                      defaultChecked={subCatForm.status === "Active"}
                    />{" "}
                    Active
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="scstatus"
                      value="Deactive"
                      defaultChecked={subCatForm.status === "Deactive"}
                    />{" "}
                    Deactive
                  </label>
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" type="submit">
                + Add
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Steps */}
      <Modal isOpen={subStepsModal} toggle={toggleSteps} backdrop="static">
        <ModalHeader toggle={toggleSteps}>Create Steps</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              swal("Steps saved (demo)");
              setStepForm({
                category_name: "",
                step_name: "",
                Description: "",
                status: "Active",
                processName: "",
              });
              toggleSteps();
            }}
          >
            <Row>
              <Col md="6">
                <Label>Steps Category</Label>
                <Input
                  type="select"
                  value={stepForm.category_name}
                  onChange={(e) =>
                    setStepForm((p) => ({
                      ...p,
                      category_name: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">-- Select Category --</option>
                  {categoryList.map((c) => (
                    <option key={c?._id} value={c?.name}>
                      {c?.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Step Name</Label>
                  <Input
                    value={stepForm.step_name}
                    onChange={(e) =>
                      setStepForm((p) => ({ ...p, step_name: e.target.value }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Step Description</Label>
                  <Input
                    type="textarea"
                    value={stepForm.Description}
                    onChange={(e) =>
                      setStepForm((p) => ({
                        ...p,
                        Description: e.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <Label className="mb-1">Status</Label>
                <div
                  onChange={(e) =>
                    setStepForm((p) => ({ ...p, status: e.target.value }))
                  }
                >
                  <label className="me-3">
                    <input
                      type="radio"
                      name="ststatus"
                      value="Active"
                      defaultChecked={stepForm.status === "Active"}
                    />{" "}
                    Active
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="ststatus"
                      value="Deactive"
                      defaultChecked={stepForm.status === "Deactive"}
                    />{" "}
                    Deactive
                  </label>
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" type="submit">
                + Add
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}
